import { Box, TextField, Typography } from "@mui/material";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { WorkstationSettingsFormValues } from "~/features/preview/workstation/hooks/useWorkstationConfigForm";
import {
  settingsOutlinedTextFieldSx,
  settingsOverviewHelperSx,
  settingsSiteSelectMaxWidthPx
} from "~/features/settings/settings.styles";

const WORKSTATION_NAME_MAX_LENGTH = 24;

export function EditWorkstationName() {
  const { t } = useTranslation();
  const {
    register,
    setValue,
    trigger,
    formState: { errors }
  } = useFormContext<WorkstationSettingsFormValues>();

  const helperMessage =
    errors.deviceId?.message ?? t("workstation name edit helper");
  const hasError = Boolean(errors.deviceId);

  const {
    onChange: onDeviceIdChange,
    onBlur: onDeviceIdBlur,
    name,
    ref
  } = register("deviceId", {
    validate: (value) => {
      if (!value.trim()) {
        return t("workstation name cannot be empty");
      }
      return true;
    }
  });

  return (
    <Box
      id="ws-edit-name-field-wrap"
      sx={{
        width: "100%",
        maxWidth: {
          xs: "100%",
          md: settingsSiteSelectMaxWidthPx
        },
        minWidth: 0,
        flex: { md: "0 1 auto" }
      }}
    >
      <TextField
        id="edit-workstation-name-input"
        fullWidth
        size="small"
        variant="outlined"
        error={hasError}
        name={name}
        inputRef={ref}
        onBlur={(event) => {
          void onDeviceIdBlur(event);
          const trimmed = event.target.value.trim();
          if (trimmed !== event.target.value) {
            setValue("deviceId", trimmed, {
              shouldDirty: true,
              shouldValidate: true
            });
          }
        }}
        onChange={(event) => {
          void onDeviceIdChange(event);
          void trigger("deviceId");
        }}
        slotProps={{
          htmlInput: {
            maxLength: WORKSTATION_NAME_MAX_LENGTH,
            "aria-label": t("edit workstation name"),
            "aria-describedby": "ws-edit-name-helper"
          }
        }}
        autoFocus
        sx={(theme) => settingsOutlinedTextFieldSx(theme)}
      />
      <Typography
        id="ws-edit-name-helper"
        variant="body2"
        sx={[
          settingsOverviewHelperSx,
          {
            color: hasError ? "error.main" : "text.secondary",
            mt: 0.5
          }
        ]}
      >
        {helperMessage}
      </Typography>
    </Box>
  );
}
